import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import FilmCard from "@/components/feature/FilmCard";
import EmptyState from "@/components/base/EmptyState";
import Stars, { SectionTitle } from "@/components/base/Rating";
import { usePageTitle, useSimulatedLoading } from "@/hooks/usePageMeta";
import {
  formatViews,
  getRelated,
  getTitleById,
  recordHistory,
  toggleFavorite,
  getFavorites,
} from "@/utils/catalog";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = getTitleById(id);
  const loading = useSimulatedLoading([id], 380);
  const [fav, setFav] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  usePageTitle(
    item ? `${item.title} - CC视频` : "影片信息不存在 - CC视频",
    item
      ? `${item.title}：${item.kind === "movie" ? "电影" : "电视剧"}，${item.genres.join("、")}，${item.year}年上映，${item.region}。${item.synopsis.slice(0, 60)}`
      : "影片信息不存在，请返回首页浏览其他影视作品。",
  );

  useEffect(() => {
    setShareOpen(false);
    setCopied(false);
    if (item) {
      setFav(getFavorites().includes(item.id));
      recordHistory(item.id);
    }
  }, [item]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  if (loading) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
              <div className="w-full aspect-[2/3] rounded-lg cc-skeleton" />
            </div>
            <div className="lg:col-span-8 space-y-4">
              <div className="h-8 w-1/2 rounded cc-skeleton" />
              <div className="h-4 w-1/3 rounded cc-skeleton" />
              <div className="h-56 w-full rounded-lg cc-skeleton" />
              <div className="h-4 w-full rounded cc-skeleton" />
              <div className="h-4 w-5/6 rounded cc-skeleton" />
            </div>
          </div>
        </div>
      </SiteLayout>
    );
  }

  if (!item) {
    return (
      <SiteLayout>
        <EmptyState
          icon="ri-file-unknow-line"
          title="影片信息不存在"
          description="该影片可能已下架，或链接地址有误。你可以返回首页继续浏览其他作品。"
        >
          <Link
            to="/"
            className="mt-6 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors inline-flex items-center whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </EmptyState>
      </SiteLayout>
    );
  }

  const related = getRelated(item, 6);
  const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
  const pathPrefix = basePath ? `/${basePath}` : "";
  const shareUrl = `${window.location.origin}${pathPrefix}/detail/${item.id}`;

  const handleFav = () => {
    const next = toggleFavorite(item.id);
    setFav(next.includes(item.id));
    window.dispatchEvent(new Event("cc-favorites-changed"));
    setToast(next.includes(item.id) ? `已收藏《${item.title}》` : `已取消收藏《${item.title}》`);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      /* clipboard not available */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <SiteLayout>
      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[70] cc-fade-in">
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-foreground-950 text-background-50 text-sm whitespace-nowrap">
            <i className="ri-checkbox-circle-fill text-accent-400 w-4 h-4 flex items-center justify-center" />
            {toast}
          </div>
        </div>
      )}

      {/* backdrop hero */}
      <section className="relative w-full bg-primary-950 overflow-hidden">
        <img
          src={item.backdrop}
          alt={`${item.title} 剧照`}
          title={`${item.title} - ${item.genres.join("、")} - CC视频`}
          className="absolute inset-0 w-full h-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/80 to-primary-950/40" />

        <div className="relative z-10 w-full px-4 md:px-6 py-8 md:py-12">
          <nav className="flex items-center gap-1.5 text-xs text-background-400 mb-6 flex-wrap">
            <Link to="/" className="hover:text-accent-400 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
            <Link
              to={`/category/${item.kind}`}
              className="hover:text-accent-400 transition-colors"
            >
              {item.kind === "movie" ? "电影" : "电视剧"}
            </Link>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
            <span className="text-accent-400 truncate max-w-[50vw]">{item.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="lg:col-span-4 xl:col-span-3">
              <div className="w-40 sm:w-52 lg:w-full mx-auto lg:mx-0 aspect-[2/3] rounded-lg overflow-hidden border border-background-50/15">
                <img
                  src={item.cover}
                  alt={`${item.title} 海报`}
                  title={`${item.title} - ${item.genres.join("、")} - CC视频`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="lg:col-span-8 xl:col-span-9 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-accent-500 text-primary-950">
                  {item.kind === "movie" ? "电影" : "电视剧"}
                </span>
                {item.tags.map((t) => (
                  <span
                    key={t}
                    className="px-2 py-0.5 rounded text-[11px] border border-background-50/25 text-background-200"
                  >
                    {t}
                  </span>
                ))}
              </div>

              <h1 className="font-heading text-2xl md:text-4xl font-black text-background-50 tracking-tight">
                {item.title}
              </h1>
              <p className="mt-1.5 text-sm text-background-400 font-label tracking-wide">
                {item.origTitle}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-background-300">
                <span className="flex items-center gap-1.5 text-accent-400">
                  <Stars rating={item.rating} size="md" />
                </span>
                <span>{item.year}</span>
                <span>{item.region}</span>
                <span>{item.duration}</span>
                <span>{formatViews(item.views)}</span>
              </div>

              <div className="mt-4 flex flex-wrap gap-1.5">
                {item.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category/${item.kind}`}
                    className="px-2.5 h-7 inline-flex items-center rounded-md bg-background-50/10 text-background-200 text-xs hover:bg-accent-500 hover:text-primary-950 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {g}
                  </Link>
                ))}
              </div>

              {/* player placeholder */}
              <div className="mt-6 w-full aspect-video rounded-lg bg-primary-900/70 border border-background-50/15 flex flex-col items-center justify-center text-center px-4">
                <span className="w-14 h-14 rounded-full bg-background-50/10 border border-background-50/25 flex items-center justify-center text-background-100 text-2xl">
                  <i className="ri-play-circle-line" />
                </span>
                <p className="mt-4 text-sm md:text-base font-medium text-background-100">
                  在线播放功能正在调试，演示区域
                </p>
                <p className="mt-1.5 text-xs text-background-400 max-w-md leading-relaxed">
                  本页面不提供任何真实视频播放与下载服务，播放器仅用于界面演示。
                </p>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2.5">
                <button
                  type="button"
                  onClick={handleFav}
                  className={`inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    fav
                      ? "bg-accent-500 text-primary-950"
                      : "bg-background-50 text-primary-950 hover:bg-background-200"
                  }`}
                >
                  <i className={fav ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                  {fav ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-background-50/30 text-background-100 text-sm hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-share-forward-line" />
                  分享
                </button>
                <Link
                  to={`/category/${item.kind}`}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-background-50/30 text-background-100 text-sm hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line" />
                  返回{item.kind === "movie" ? "电影" : "剧集"}列表
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-6 py-8 md:py-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 min-w-0">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">剧情简介</h2>
            <p className="text-sm md:text-[15px] text-foreground-700 leading-[1.9] md:leading-[2]">
              {item.synopsis}
            </p>
          </div>

          <div className="mt-4 md:mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">影片信息</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3.5 text-sm">
              {[
                { k: "导演", v: item.director },
                { k: "主演", v: item.cast.join(" / ") },
                { k: "类型", v: item.genres.join(" / ") },
                { k: "地区", v: item.region },
                { k: "上映时间", v: `${item.year} 年` },
                { k: "片长", v: item.duration },
              ].map((row) => (
                <div key={row.k} className="flex gap-3 items-start">
                  <dt className="w-16 shrink-0 text-foreground-500">{row.k}</dt>
                  <dd className="text-foreground-900 leading-relaxed">{row.v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
            <h2 className="font-heading text-sm font-bold text-foreground-950 mb-3">
              作品信息速览
            </h2>
            <div className="space-y-3 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-foreground-500">综合评分</span>
                <span className="font-label font-semibold text-accent-700 text-sm">
                  {item.rating.toFixed(1)} / 5.0
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-foreground-500">播放热度</span>
                <span className="font-label text-foreground-900">{formatViews(item.views)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-foreground-500">内容形式</span>
                <span className="text-foreground-900">
                  {item.kind === "movie" ? "院线电影" : "剧集"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-foreground-500">播放状态</span>
                <span className="text-foreground-500">演示区域</span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-background-200 text-[11px] text-foreground-500 leading-relaxed">
              站内信息为演示数据，海报与简介仅用于界面展示，不提供真实播放。
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <h2 className="font-heading text-sm font-bold text-foreground-950 mb-3">
              按类型浏览
            </h2>
            <div className="flex flex-wrap gap-1.5">
              {Array.from(new Set([...item.genres, "悬疑", "剧情", "科幻", "治愈"])).map((g) => (
                <Link
                  key={g}
                  to={`/search?q=${encodeURIComponent(g)}`}
                  className="px-2.5 h-7 inline-flex items-center rounded-md bg-background-100 text-foreground-700 text-xs hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {g}
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>

      <section className="w-full px-4 md:px-6">
        <SectionTitle
          accentLabel="You May Also Like"
          title="相关推荐"
          subtitle="基于类型与导演为你匹配的相似作品。"
          moreTo={`/category/${item.kind}`}
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-5">
          {related.map((r, i) => (
            <div key={r.id} className="cc-fade-up">
              <FilmCard item={r} index={i} />
            </div>
          ))}
        </div>
      </section>

      {shareOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
          <button
            type="button"
            aria-label="关闭分享"
            onClick={() => setShareOpen(false)}
            className="absolute inset-0 bg-primary-950/60 cursor-pointer"
          />
          <div className="relative w-full max-w-md rounded-lg bg-background-50 p-5 md:p-6 cc-fade-in">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-heading text-base font-bold text-foreground-950">分享这部作品</h3>
                <p className="mt-1 text-xs text-foreground-500">
                  {item.title} · {item.year}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShareOpen(false)}
                aria-label="关闭"
                className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="flex items-center gap-2 p-2 rounded-md bg-background-100 border border-background-200">
              <input
                readOnly
                value={shareUrl}
                className="flex-1 min-w-0 bg-transparent px-2 text-xs text-foreground-700 outline-none"
              />
              <button
                type="button"
                onClick={handleCopy}
                className="h-8 px-3.5 rounded-md bg-accent-500 text-primary-950 text-xs font-medium whitespace-nowrap cursor-pointer"
              >
                {copied ? "已复制" : "复制链接"}
              </button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { icon: "ri-weibo-fill", label: "微博" },
                { icon: "ri-wechat-fill", label: "微信" },
                { icon: "ri-link", label: "其他" },
              ].map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 h-9 px-3 rounded-md border border-background-200 text-xs text-foreground-700 hover:border-secondary-400 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className={`${s.icon} w-4 h-4 flex items-center justify-center`} />
                  {s.label}
                </button>
              ))}
            </div>
            <p className="mt-4 text-[11px] text-foreground-400 leading-relaxed">
              链接为本站演示地址，不接入第三方分享 SDK。
            </p>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}